import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import type { User, Session } from '@supabase/supabase-js';

interface Profile {
  id: string;
  full_name: string;
  role: string;
  plan: string;
  avatar_url: string | null;
  company_id: string | null;
  connection_type: string;
  credits: number;
  monthly_message_limit: number;
  messages_sent_this_month: number;
  onboarding_completed: boolean;
  created_at: string;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string, fullName: string, redirectTo?: string) => Promise<{ error: string | null; data?: { session: Session | null } }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

async function ensureProfile(user: User) {
  const fullName = user.user_metadata?.full_name ?? user.email ?? 'Usuário';

  // Check if profile already exists
  const { data: existing } = await supabase
    .from('profiles')
    .select('id, company_id, onboarding_completed, role, created_at')
    .eq('id', user.id)
    .maybeSingle();

  if (existing?.id) {
    return existing as Profile;
  }

  // Create company
  const { data: company, error: companyError } = await supabase
    .from('companies')
    .insert({ name: 'Minha Empresa' })
    .select('id')
    .single();

  if (companyError || !company) {
    // If company creation fails, still create profile with no company
    // This prevents mixing users into other companies
    await supabase.from('profiles').insert({
      id: user.id,
      full_name: fullName,
      role: 'user',
      plan: 'trial',
      credits: 0,
      monthly_message_limit: 500,
      company_id: null,
    });
    const { data: profile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();
    return profile as Profile;
  }

  // Create profile linked to company
  await supabase.from('profiles').insert({
    id: user.id,
    full_name: fullName,
    role: 'user',
    plan: 'trial',
    credits: 0,
    monthly_message_limit: 500,
    company_id: company.id,
  });

  const { data: profile } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', user.id)
    .maybeSingle();

  return profile as Profile;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (currentUser: User) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, full_name, role, plan, avatar_url, company_id, connection_type, credits, monthly_message_limit, messages_sent_this_month, onboarding_completed, created_at')
      .eq('id', currentUser.id)
      .maybeSingle();

    if (!error && data) {
      setProfile(data as Profile);
      return data as Profile;
    }

    // Auto-create if missing
    const created = await ensureProfile(currentUser);
    if (created) {
      setProfile(created);
    }
    return created;
  };

  useEffect(() => {
    const init = async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        if (currentSession?.user) {
          fetchProfile(currentSession.user).catch(() => setProfile(null));
        }
      } catch {
        // If getSession throws (e.g. invalid refresh token), just stay logged out
        setSession(null);
        setUser(null);
        setProfile(null);
      }
      setLoading(false);
    };
    init();

    const { data: listener } = supabase.auth.onAuthStateChange((_event, newSession) => {
      // If Supabase signs us out (e.g. invalid refresh token), clean up
      if (_event === 'SIGNED_OUT') {
        setUser(null);
        setSession(null);
        setProfile(null);
        setLoading(false);
        return;
      }

      if (_event === 'TOKEN_REFRESHED') {
        setSession(newSession);
        setUser(newSession?.user ?? null);
        // Don't re-fetch profile on every token refresh — just update session/user
        return;
      }

      setSession(newSession);
      setUser(newSession?.user ?? null);
      setLoading(true);
      if (newSession?.user) {
        fetchProfile(newSession.user)
          .catch(() => setProfile(null))
          .finally(() => setLoading(false));
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error, data } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      return { error: error.message };
    }
    if (data.user) {
      await fetchProfile(data.user);
    }
    return { error: null };
  };

  const signUp = async (email: string, password: string, fullName: string, redirectTo?: string) => {
    const { error, data } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName }, emailRedirectTo: redirectTo },
    });
    if (error) {
      return { error: error.message };
    }

    // Auto-create profile/company after signup (trigger may have failed silently)
    if (data.user) {
      await ensureProfile(data.user);
    }

    return { error: null, data: { session: data.session } };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email);
    return { error: error?.message ?? null };
  };

  const refreshProfile = async () => {
    if (!user) return;
    await fetchProfile(user);
  };

  return (
    <AuthContext.Provider value={{ user, session, profile, loading, signIn, signUp, signOut, resetPassword, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { AuthContext };